import { useState } from "react";
import { useNotification } from "./useNotification";
import { ProjectsService } from "@root/services/projects";
import {
  CHAT_TYPE,
  FILE_TYPE,
  PAGINATION_DEFAULTS,
  TOAST_VARIANT,
  VIEW_TYPE,
} from "@root/constants";
import { useTranslation } from "react-i18next";

export const useProjects = () => {
  // Projects state
  const [projectsData, setProjectsData] = useState<any>(null);
  const [projectData, setProjectData] = useState(null);
  const [addProjectLoading, setAddProjectLoading] = useState(false);
  const [updateProjectLoading, setUpdateProjectLoading] = useState(false);
  const [deleteProjectLoading, setDeleteProjectLoading] = useState(false);
  const [getProjectsLoading, setGetProjectsLoading] = useState(false);
  const [getProjectLoading, setGetProjectLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // Folders state
  const [foldersData, setFoldersData] = useState(null);
  const [folderData, setFolderData] = useState(null);
  const [addFolderLoading, setAddFolderLoading] = useState(false);
  const [updateFolderLoading, setUpdateFolderLoading] = useState(false);
  const [deleteFolderLoading, setDeleteFolderLoading] = useState(false);
  const [getFoldersLoading, setGetFoldersLoading] = useState(false);
  const [getFolderLoading, setGetFolderLoading] = useState(false);
  const [folderCount, setFolderCount] = useState(1);

  // Documents state
  const [documentsData, setDocumentsData] = useState(null);
  const [documentData, setDocumentData] = useState<any>(null);
  const [addDocumentLoading, setAddDocumentLoading] = useState(false);
  const [updateDocumentLoading, setUpdateDocumentLoading] = useState(false);
  const [deleteDocumentLoading, setDeleteDocumentLoading] = useState(false);
  const [getDocumentsLoading, setGetDocumentsLoading] = useState(false);
  const [getDocumentLoading, setGetDocumentLoading] = useState(false);
  const [documentCount, setDocumentCount] = useState(1);

  // Others
  const [view, setView] = useState(VIEW_TYPE.PROJECT);

  const projectService = new ProjectsService();
  const { showNotification } = useNotification();
  const { t } = useTranslation("common");

  const payload = {
    limit: PAGINATION_DEFAULTS.PAGESIZE,
    page,
    search: search,
  };

  const projectsOptions = projectsData?.rows
    ?.filter((project: any) => project?.id !== 3)
    ?.map((project: any) => ({
      value: project?.id,
      label: project?.name,
    }));

  // Projects functions
  const fetchProjects = async (payload: any) => {
    setGetProjectsLoading(true);
    try {
      const res = await projectService.fetchProjects(payload);
      setProjectsData(res?.data?.data);
    } catch (error: unknown) {
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: t("projects.projectsFailedError"),
      });
    } finally {
      setGetProjectsLoading(false);
    }
  };

  const fetchProject = async (id: number) => {
    setGetProjectLoading(true);
    try {
      const res = await projectService.fetchProject(id);
      setProjectData(res?.data?.data);
    } catch (error: unknown) {
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: t("projects.projectFailedError"),
      });
    } finally {
      setGetProjectLoading(false);
    }
  };

  const updateProject = async (id: number, payloadInner: any) => {
    setUpdateProjectLoading(true);
    try {
      await projectService.updateProject(id, payloadInner);
      showNotification({
        type: TOAST_VARIANT.SUCCESS,
        message: t("projects.projectUpdatedSuccess"),
      });
      fetchProjects(payload);
    } catch (err) {
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: t("projects.projectUpdateFailedError"),
      });
    } finally {
      setUpdateProjectLoading(false);
    }
  };

  const addProject = async (payload: any) => {
    setAddProjectLoading(true);
    try {
      await projectService.addProject(payload);
      showNotification({
        type: TOAST_VARIANT.SUCCESS,
        message: t("projects.projectAddedSuccess"),
      });
      fetchProjects(payload);
    } catch (err) {
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: t("projects.projectAddFailedError"),
      });
    } finally {
      setAddProjectLoading(false);
    }
  };

  const deleteProject = async (id: number) => {
    setDeleteProjectLoading(true);
    try {
      await projectService.deleteProject(id);
      showNotification({
        type: TOAST_VARIANT.SUCCESS,
        message: t("projects.projectDeletedSuccess"),
      });
      fetchProjects(payload);
    } catch (err) {
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: t("projects.projectDeleteFailedError"),
      });
    } finally {
      setDeleteProjectLoading(false);
    }
  };

  function handleProjectClick(project: any) {
    setProjectData(project);
    setView(VIEW_TYPE.FOLDER);
    const payload = {
      projectId: project?.id,
      limit: PAGINATION_DEFAULTS.PAGESIZE,
      page,
    };
    fetchFolders(payload);
  }

  // Folders functions
  const fetchFolders = async (payload: any) => {
    setGetFoldersLoading(true);
    try {
      const res = await projectService.fetchFolders(payload);
      setFoldersData(res?.data?.data);
      setFolderCount(res?.data?.data?.count);
    } catch (error: unknown) {
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: t("projects.foldersFailedError"),
      });
    } finally {
      setGetFoldersLoading(false);
    }
  };

  const fetchFolder = async (id: number) => {
    setGetFolderLoading(true);
    try {
      const res = await projectService.fetchFolder(id);
      setFolderData(res?.data?.data);
    } catch (error: unknown) {
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: t("projects.folderFailedError"),
      });
    } finally {
      setGetFolderLoading(false);
    }
  };

  const updateFolder = async (id: number, payloadInner: any) => {
    setUpdateFolderLoading(true);
    try {
      await projectService.updateFolder(id, payloadInner);
      showNotification({
        type: TOAST_VARIANT.SUCCESS,
        message: t("projects.folderUpdatedSuccess"),
      });
      fetchFolders(payload);
    } catch (err) {
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: t("projects.folderUpdateFailedError"),
      });
    } finally {
      setUpdateFolderLoading(false);
    }
  };

  const addFolder = async (payload: any) => {
    setAddFolderLoading(true);
    try {
      await projectService.addFolder(payload);
      showNotification({
        type: TOAST_VARIANT.SUCCESS,
        message: t("projects.folderAddedSuccess"),
      });
      fetchFolders(payload);
    } catch (err) {
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: t("projects.folderAddFailedError"),
      });
    } finally {
      setAddFolderLoading(false);
    }
  };

  const deleteFolder = async (id: number) => {
    setDeleteFolderLoading(true);
    try {
      await projectService.deleteFolder(id);
      showNotification({
        type: TOAST_VARIANT.SUCCESS,
        message: t("projects.folderDeletedSuccess"),
      });
      fetchFolders(payload);
    } catch (err) {
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: t("projects.folderDeleteFailedError"),
      });
    } finally {
      setDeleteFolderLoading(false);
    }
  };

  function handleFolderClick(folder: any) {
    setFolderData(folder);
    setView(VIEW_TYPE.DOCUMENT);
    const payload = {
      folderId: folder?.id,
      limit: PAGINATION_DEFAULTS.PAGESIZE,
      page,
    };
    fetchDocuments(payload);
  }

  // Documents functions
  const fetchDocuments = async (payload: any) => {
    setGetDocumentsLoading(true);
    try {
      const res = await projectService.fetchDocuments(payload);
      setDocumentsData(res?.data?.data);
      setDocumentCount(res?.data?.data?.count);
    } catch (error: unknown) {
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: t("projects.documentsFailedError"),
      });
    } finally {
      setGetDocumentsLoading(false);
    }
  };

  const fetchDocument = async (id: number) => {
    setGetDocumentLoading(true);
    try {
      const res = await projectService.fetchDocument(id);
      setDocumentData(res?.data?.data);
    } catch (error: unknown) {
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: t("projects.documentFailedError"),
      });
    } finally {
      setGetDocumentLoading(false);
    }
  };

  const updateDocument = async (id: number, payloadInner: any) => {
    setUpdateDocumentLoading(true);
    try {
      await projectService.updateDocument(id, payloadInner);
      showNotification({
        type: TOAST_VARIANT.SUCCESS,
        message: t("projects.documentUpdatedSuccess"),
      });
      fetchDocuments(payload);
    } catch (err) {
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: t("projects.documentUpdateFailedError"),
      });
    } finally {
      setUpdateDocumentLoading(false);
    }
  };

  const addDocument = async (payload: any) => {
    setAddDocumentLoading(true);
    try {
      await projectService.addDocument(payload);
      showNotification({
        type: TOAST_VARIANT.SUCCESS,
        message: t("projects.documentAddedSuccess"),
      });
      fetchDocuments(payload);
    } catch (err) {
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: t("projects.documentAddFailedError"),
      });
    } finally {
      setAddDocumentLoading(false);
    }
  };

  const deleteDocument = async (id: number) => {
    setDeleteDocumentLoading(true);
    try {
      await projectService.deleteDocument(id);
      showNotification({
        type: TOAST_VARIANT.SUCCESS,
        message: t("projects.documentDeletedSuccess"),
      });
      fetchDocuments(payload);
    } catch (err) {
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: t("projects.documentDeleteFailedError"),
      });
    } finally {
      setDeleteDocumentLoading(false);
    }
  };

  const handleBackClick = () => {
    if (view === VIEW_TYPE.DOCUMENT) {
      setDocumentsData(null);
      setView(VIEW_TYPE.FOLDER);
    } else if (view === VIEW_TYPE.FOLDER) {
      setFoldersData(null);
      setView(VIEW_TYPE.PROJECT);
    }
  };

  const getFilename = () => {
    if (documentData?.type === FILE_TYPE.DRAWING_ANALYSIS) {
      return documentData?.fileName;
    }
    return undefined;
  };

  return {
    // Projects
    addProjectLoading,
    getProjectsLoading,
    getProjectLoading,
    updateProjectLoading,
    deleteProjectLoading,
    addProject,
    updateProject,
    deleteProject,
    fetchProjects,
    fetchProject,
    projectsData,
    setProjectsData,
    projectData,
    setProjectData,
    search,
    setSearch,
    page,
    setPage,

    // Folders
    addFolderLoading,
    getFoldersLoading,
    getFolderLoading,
    updateFolderLoading,
    deleteFolderLoading,
    addFolder,
    updateFolder,
    deleteFolder,
    fetchFolders,
    fetchFolder,
    foldersData,
    setFoldersData,
    folderData,
    setFolderData,
    folderCount,

    // Documents
    addDocumentLoading,
    getDocumentsLoading,
    getDocumentLoading,
    updateDocumentLoading,
    deleteDocumentLoading,
    addDocument,
    updateDocument,
    deleteDocument,
    fetchDocuments,
    fetchDocument,
    documentsData,
    setDocumentsData,
    documentData,
    setDocumentData,
    view,
    setView,
    handleProjectClick,
    handleFolderClick,
    handleBackClick,
    projectsOptions,
    documentCount,
    getFilename,
  };
};
